<template>
  <section class="UserModal">
    <Modal
      v-if="isOpenReSendConfirmModal"
      ref="modal"
      class="UserModal__modal"
      :heading="messages.user.reSendInvitationEmail"
      :size="isExtraSmallPixel ? 'extraSmall' : 'small'"
      @closeModal="onClickReSendConfirmModalClose"
    >
      <p class="UserModal__modalText">
        {{ messages.user.confirm.reSendInvitation }}
      </p>
      <ul class="UserModal__modalButton">
        <li class="UserModal__modalItem">
          <Button
            :label="messages.common.cancel"
            dynamics="secondary"
            tag="button"
            @click="onClickReSendConfirmModalClose"
          />
        </li>
        <li class="UserModal__modalItem">
          <Button
            :label="messages.common.send"
            dynamics="primary"
            tag="button"
            @click="onClickReSendConfirmButton"
          />
        </li>
      </ul>
    </Modal>
    <Modal
      v-if="isOpenReSendModal"
      ref="modal"
      class="UserModal__modal"
      :heading="
        isReSendSuccess ? messages.user.reSendCompleted : messages.user.reSend
      "
      :size="isExtraSmallPixel ? 'extraSmall' : 'small'"
    >
      <img
        v-if="isReSendSuccess"
        class="UserModal__modalImg"
        src="@spectee/assets/images/img_send.png"
        alt=""
        width="48"
        height="39"
      />
      <Icon v-else class="UserModal__icon" iconName="Exception"></Icon>
      <!-- safe usage of v-html: content is fully static and developer-controlled -->
      <p
        class="UserModal__modalText"
        v-html="isReSendSuccess ? reSendSuccessMsg : reSendFailMsg"
      ></p>
      <ul class="UserModal__modalButton">
        <li class="UserModal__modalItem">
          <Button
            :label="messages.common.close"
            dynamics="secondary"
            tag="button"
            @click="onClickReSendModalClose"
          />
        </li>
      </ul>
    </Modal>
    <Modal
      v-if="isOpenDeleteModal"
      ref="modal"
      class="UserModal__modal"
      :heading="messages.user.deleteUser"
      :size="isExtraSmallPixel ? 'extraSmall' : 'small'"
    >
      <template v-if="isShowDeleteWarning">
        <!-- safe usage of v-html: content is fully static and developer-controlled -->
        <p
          class="UserModal__modalText"
          v-html="messages.user.confirm.deleteUser"
        ></p>

        <ul class="UserModal__modalButton">
          <li class="UserModal__modalItem">
            <Button
              :isDisabled="isDeleteApiLoading"
              :label="messages.common.cancel"
              dynamics="secondary"
              tag="button"
              @click="onClickCancelButton"
            />
          </li>
          <li class="UserModal__modalItem">
            <Button
              :isDisabled="isDeleteApiLoading"
              :label="messages.common.delete"
              dynamics="danger"
              tag="button"
              @click="onClickDeleteDone"
            />
          </li>
        </ul>
      </template>
      <template v-else-if="isDeleteError">
        <Icon class="UserModal__icon" iconName="Exception"></Icon>
        <!-- safe usage of v-html: content is fully static and developer-controlled -->
        <p class="UserModal__modalText" v-html="deleteErrorMsg"></p>
        <div class="UserModal__modalButton">
          <Button
            :label="messages.common.close"
            dynamics="secondary"
            tag="button"
            @click="onClickDeleteClose"
          />
        </div>
      </template>
    </Modal>
    <Modal
      v-if="isCreateError || isUpdateError"
      ref="modal"
      class="UserModal__modal"
      :heading="
        isCreateError
          ? messages.user.userInvitation.title
          : messages.user.editUserDetail
      "
      :size="isExtraSmallPixel ? 'extraSmall' : 'small'"
    >
      <Icon class="UserModal__icon" iconName="Exception"></Icon>
      <!-- safe usage of v-html: content is fully static and developer-controlled -->
      <p
        class="UserModal__modalText"
        v-html="isCreateError ? createErrorMsg : updateErrorMsg"
      ></p>
      <div class="UserModal__modalButton">
        <Button
          :label="messages.common.close"
          dynamics="secondary"
          tag="button"
          @click="onClickSaveErrorClose"
        />
      </div>
    </Modal>
    <Modal
      v-if="isShowPasswordResetModal"
      ref="modal"
      class="UserModal__modal"
      :heading="messages.user.passwordReset"
      :size="isExtraSmallPixel ? 'extraSmall' : 'small'"
    >
      <Icon
        v-if="!isPasswordResetSuccess"
        class="UserModal__icon"
        iconName="Exception"
      ></Icon>
      <!-- safe usage of v-html: content is fully static and developer-controlled -->
      <p
        class="UserModal__modalText"
        v-html="
          isPasswordResetSuccess
            ? passwordResetSuccessMsg
            : passwordResetErrorMsg
        "
      ></p>
      <p
        v-if="isPasswordResetSuccess"
        class="UserModal__modalSubText"
        v-html="passwordResetSuccessMsg2"
      ></p>
      <div class="UserModal__modalButton">
        <Button
          :label="messages.common.close"
          dynamics="secondary"
          tag="button"
          @click="onClickPasswordResetModalClose"
        />
      </div>
    </Modal>
  </section>
</template>

<script setup lang="ts">
import Modal from '@spectee/components/Organisms/Modal/index.vue';
import Button from '@spectee/components/Atoms/Button/index.vue';
import Icon from '@spectee/components/Atoms/IconBase/index.vue';
import { useWindowWidth } from '@spectee/composables/useWindowWidth';
import messages from '../../../../messages.json';
import { computed } from 'vue';

defineExpose({
  name: 'UserModal',
});

interface Props {
  isDeleteError?: boolean;
  isShowDeleteWarning?: boolean;
  isOpenReSendConfirmModal?: boolean;
  isOpenReSendModal?: boolean;
  isReSendSuccess?: boolean;
  isOpenDeleteModal?: boolean;
  isDeleteApiLoading?: boolean;
  isCreateError?: boolean;
  isUpdateError?: boolean;
  isShowPasswordResetModal?: boolean;
  isPasswordResetSuccess?: boolean;
  failedInviteUserEmails?: string[];
  failedDeleteUserEmails?: string[];
}

const { isExtraSmallPixel } = useWindowWidth();

const props = withDefaults(defineProps<Props>(), {
  failedInviteUserEmails: () => [],
  failedDeleteUserEmails: () => [],
});

const emit = defineEmits([
  'onClickReSendConfirmModalClose',
  'onClickReSendConfirmButton',
  'onClickReSendModalClose',
  'onClickCancelButton',
  'onClickDeleteDone',
  'onClickDeleteClose',
  'onClickSaveErrorClose',
  'onClickPasswordResetModalClose',
]);

const onClickReSendConfirmModalClose = () => {
  emit('onClickReSendConfirmModalClose');
};

const onClickReSendConfirmButton = () => {
  emit('onClickReSendConfirmButton');
};

const onClickReSendModalClose = () => {
  emit('onClickReSendModalClose');
};
const onClickCancelButton = () => {
  emit('onClickCancelButton');
};
const onClickDeleteDone = () => {
  emit('onClickDeleteDone');
};
const onClickDeleteClose = () => {
  emit('onClickDeleteClose');
};
const onClickSaveErrorClose = () => {
  emit('onClickSaveErrorClose');
};
const onClickPasswordResetModalClose = () => {
  emit('onClickPasswordResetModalClose');
};

const reSendSuccessMsg = messages.user.success.reSendInvitation;
const reSendFailMsg = messages.user.fail.reSendInvitation;
const updateErrorMsg = messages.user.fail.userUpdate;
const passwordResetSuccessMsg = messages.user.success.passwordResetMsg1;
const passwordResetSuccessMsg2 = messages.user.success.passwordResetMsg2;
const passwordResetErrorMsg = messages.user.fail.passwordResetMsg;

const deleteErrorMsg = computed(() => {
  const failDelete = messages.user.fail.userDelete;
  const failDeleteIds = messages.user.fail.userDeleteID;
  const failedIds = props.failedDeleteUserEmails ?? [];

  if (!failedIds.length) return failDelete;

  const joined = failedIds.join(', ');

  return `${failDeleteIds}: ${joined}`;
});

const createErrorMsg = computed(() => {
  const failInvite = messages.user.fail.userCreate;
  const failInviteIds = messages.user.fail.userCreateID;
  const failedIds = props.failedInviteUserEmails ?? [];

  if (!failedIds.length) return failInvite;

  const joined = failedIds.join(', ');

  return `${failInviteIds}: ${joined}`;
});
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;
.UserModal {
  &__modalImg,
  &__modalText,
  &__modalButton {
    margin-top: $service-unit * 6;
  }

  & &__modal,
  &__modalText,
  &__modalSubText {
    text-align: center;
  }

  &__modalText {
    font-size: $service-font-size-p;
    line-height: $service-line-height-mp;
  }

  &__modalSubText {
    margin-top: $service-unit * 3;
    font-size: $service-font-size-mp;
    line-height: $service-line-height-mp;
    color: $color-gray-60;
  }

  &__modalButton {
    display: flex;
    justify-content: center;
  }

  &__modalItem:not(:first-child) {
    margin-left: $service-unit * 3;
  }

  &__icon {
    width: $service-unit * 10;
    margin: $service-unit * 4 $service-unit * 38 0 $service-unit * 38;
    fill: #c33b2e;
  }

  @media (max-width: 376px) {
    &__icon {
      margin-left: $service-unit * 28;
    }
  }
}
</style>
